import { useReducer, useCallback } from 'react';
import { GameState } from '../types/game';
import { DEFAULT_SETTINGS, calculateScore } from '../utils/gameLogic';

type GameAction = 
  | { type: 'START_GAME' }
  | { type: 'START_WAITING' }
  | { type: 'SHOW_SIGNAL' }
  | { type: 'HANDLE_CLICK'; timestamp: number }
  | { type: 'FINISH_ROUND' }
  | { type: 'RESET_GAME' }
  | { type: 'TIMEOUT' };

const initialState: GameState = {
  status: 'idle',
  reactionTime: null,
  currentScore: 0,
  round: 0,
  startTime: null,
  signalTime: null,
  roundReactionTimes: [],
};

const gameReducer = (state: GameState, action: GameAction): GameState => {
  console.log('Reducer:', action.type, 'current status:', state.status);
  switch (action.type) {
    case 'START_GAME':
      return {
        status: 'waiting',
        reactionTime: null,
        currentScore: 0,
        round: 1,
        startTime: Date.now(),
        signalTime: null,
        roundReactionTimes: [],
      };
    
    case 'START_WAITING':
      return {
        ...state,
        status: 'waiting',
        signalTime: null,
        reactionTime: null,
      };
    
    case 'SHOW_SIGNAL':
      return {
        ...state,
        status: 'ready',
        signalTime: Date.now(),
      };
    
    case 'HANDLE_CLICK':
      if (state.status !== 'ready' || !state.signalTime) {
        return state;
      }
      
      const reactionTime = action.timestamp - state.signalTime;
      const score = calculateScore(reactionTime);
      const updatedReactionTimes = [...state.roundReactionTimes, reactionTime];
      
      return {
        ...state,
        status: 'finished',
        reactionTime,
        currentScore: state.currentScore + score,
        roundReactionTimes: updatedReactionTimes,
      };
    
    case 'FINISH_ROUND':
      if (state.round >= DEFAULT_SETTINGS.maxRounds) {
        return {
          ...state,
          status: 'finished',
        };
      }
      
      return {
        ...state,
        status: 'waiting',
        round: state.round + 1,
        signalTime: null,
        reactionTime: null,
      };
    
    case 'RESET_GAME':
      return {
        ...initialState,
        roundReactionTimes: [],
      };
    
    case 'TIMEOUT':
      return {
        ...state,
        status: 'finished',
        reactionTime: null,
      };
    
    default:
      return state;
  }
};

export const useGameState = () => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  
  const startGame = useCallback(() => {
    console.log('startGame called');
    dispatch({ type: 'START_GAME' });
  }, []);
  
  const startWaiting = useCallback(() => {
    dispatch({ type: 'START_WAITING' });
  }, []);
  
  const showSignal = useCallback(() => {
    dispatch({ type: 'SHOW_SIGNAL' });
  }, []);
  
  const handleClick = useCallback(() => {
    dispatch({ type: 'HANDLE_CLICK', timestamp: Date.now() });
  }, []);
  
  const finishRound = useCallback(() => {
    dispatch({ type: 'FINISH_ROUND' });
  }, []);
  
  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
  }, []);
  
  const timeout = useCallback(() => {
    dispatch({ type: 'TIMEOUT' });
  }, []);

  // 최소 반응시간 계산
  const getBestReactionTime = useCallback(() => {
    if (state.roundReactionTimes.length === 0) return null;
    return Math.min(...state.roundReactionTimes);
  }, [state.roundReactionTimes]);

  // 게임 완료 여부 확인
  const isGameComplete = useCallback(() => {
    return state.round >= DEFAULT_SETTINGS.maxRounds && state.status === 'finished';
  }, [state.round, state.status]);
  
  return {
    ...state,
    startGame,
    startWaiting,
    showSignal,
    handleClick,
    finishRound,
    resetGame,
    timeout,
    getBestReactionTime,
    isGameComplete,
  };
};